import React, { useState, useEffect } from "react";
import { EditButton, DeleteButton, ShowButton } from "@refinedev/antd";
import { useList } from "@refinedev/core";
import { Space, Table, Tag, Card, Input } from "antd";
import { SearchOutlined, PlusOutlined, TagOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface Tax {
    id: number;
    taxTypeId: string;
    taxType: string;
    rate: number;
    isActive: boolean;
    createdAt: string;
}

export const TaxesList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<Tax[]>([]);
    const navigate = useNavigate();

    const { data, isLoading } = useList({
        resource: "taxes",
    });

    useEffect(() => {
        if (!data?.data) return;

        let taxes = data.data as Tax[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            taxes = taxes.filter((tax: Tax) =>
                tax.taxType?.toLowerCase().includes(searchLower) ||
                tax.taxTypeId?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...taxes].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const columns = [
        {
            title: "Tax Type",
            dataIndex: "taxType",
            key: "taxType",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value}</span>
            ),
        },
        {
            title: "Tax ID",
            dataIndex: "taxTypeId",
            key: "taxTypeId",
            render: (value: string) => (
                <span className="text-text-secondary">{value || "-"}</span>
            ),
        },
        {
            title: "Rate",
            dataIndex: "rate",
            key: "rate",
            render: (value: number) => (
                <span className="font-semibold text-primary">{value}%</span>
            ),
        },
        {
            title: "Status",
            dataIndex: "isActive",
            key: "status",
            render: (value: boolean) => (
                <Tag color={value ? "success" : "default"} className="status-tag">
                    {value ? "Active" : "Inactive"}
                </Tag>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 120,
            render: (_: any, record: Tax) => (
                <Space size="small">
                    <EditButton hideText size="small" recordItemId={record.id} />
                    <ShowButton hideText size="small" recordItemId={record.id} />
                    <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Taxes"
                icon={<TagOutlined />}
                breadcrumbs={[{ title: "Taxes" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/taxes/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by tax type or ID"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 600 }}
                    />
                </Card>
            </div>
        </div>
    );
};
